import {
  UserTierTypes,
  formatNumber,
  useIsDesktop,
} from '@eCommerce/frontend-legacy-shared-library-native';

import {Text, View} from 'native-base';

import React from 'react';
import {useTheme} from 'styled-components/native';

export interface NextTierCreditsProps {
  currentLevel: UserTierTypes;
  remainingCreditForNextLevel: number;
  nextTier?: string;
  isGiftedTier: boolean;
  hideLabel?: boolean;
  byInvitation?: boolean;
  invitationRequired?: boolean;
  tierCredits?: number;
  isInOlympusBand?: boolean;
}

const OLYMPUS_TARGET = 300000;

const NextTierCredits: React.FC<NextTierCreditsProps> = props => {
  const {
    currentLevel,
    remainingCreditForNextLevel,
    nextTier,
    hideLabel = false,
    byInvitation = false,
    invitationRequired = false,
    tierCredits = 0,
    isInOlympusBand = false,
  } = props;

  const theme = useTheme();
  const {white, shuttleGray} = theme.colors;
  const isDesktop = useIsDesktop();
  const onDark = isDesktop && currentLevel === UserTierTypes.SEVEN_STARS;
  const color = onDark ? white : shuttleGray;
  const fontSize = isDesktop ? '14px' : '11px';

  if (hideLabel) {
    return null;
  }

  // Olympus 300k threshold logic — mirrors CreditIndicator
  if (invitationRequired || byInvitation) {
    const hasReachedOlympus = tierCredits >= OLYMPUS_TARGET;

    if (isDesktop) {
      return (
        <View alignItems="center">
          <Text
            fontFamily="body"
            fontSize={fontSize}
            fontWeight="bold"
            color={color}>
            {hasReachedOlympus
              ? 'Olympus'
              : formatNumber(remainingCreditForNextLevel)}
          </Text>
          <Text fontFamily="body" fontSize={fontSize} color={color}>
            {hasReachedOlympus
              ? 'Pending Invitation'
              : 'to Olympus Eligibility'}
          </Text>
        </View>
      );
    }

    return (
      <View alignItems="flex-end">
        <Text
          fontFamily="body"
          fontSize={fontSize}
          fontWeight="bold"
          color={color}>
          {hasReachedOlympus
            ? 'Olympus'
            : formatNumber(remainingCreditForNextLevel)}
        </Text>
        <Text fontFamily="body" fontSize={fontSize} color={color}>
          {hasReachedOlympus ? 'Pending Invitation' : 'to Olympus Eligibility'}
        </Text>
      </View>
    );
  }

  // Standard tier progression (non-Olympus path)
  const showsTarget = !!nextTier;
  const showsCountdown = showsTarget && remainingCreditForNextLevel > 0;

  if (!showsTarget) {
    return null;
  }

  if (isDesktop) {
    return (
      <View alignItems="center">
        <View flexDirection="row" flexWrap="wrap" justifyContent="center">
          {showsCountdown ? (
            <Text
              fontFamily="body"
              fontSize={fontSize}
              fontWeight="bold"
              color={color}>
              {formatNumber(remainingCreditForNextLevel)}{' '}
            </Text>
          ) : null}
          <Text fontFamily="body" fontSize={fontSize} color={color}>
            {showsCountdown ? `to ${nextTier}` : nextTier}
          </Text>
        </View>
      </View>
    );
  }

  // Mobile: when in the Olympus band (Seven Stars / near-Olympus), stack number
  // and label on separate lines to mirror how CurrentTierCredits lays out on
  // the left side. Otherwise keep them inline on a single row.
  if (isInOlympusBand) {
    return (
      <View alignItems="flex-end">
        {showsCountdown ? (
          <>
            <Text
              fontFamily="body"
              fontSize={fontSize}
              fontWeight="bold"
              color={color}>
              {formatNumber(remainingCreditForNextLevel)}
            </Text>
            <Text
              fontFamily="body"
              fontSize={fontSize}
              color={color}
              textAlign="right">
              {`to ${nextTier}`}
            </Text>
          </>
        ) : (
          <Text
            fontFamily="body"
            fontSize={fontSize}
            color={color}
            textAlign="right">
            {nextTier}
          </Text>
        )}
      </View>
    );
  }

  return (
    <View flexDirection="row" alignItems="center" justifyContent="flex-end">
      {showsCountdown ? (
        <>
          <Text
            fontFamily="body"
            fontSize={fontSize}
            fontWeight="bold"
            color={color}>
            {formatNumber(remainingCreditForNextLevel)}
          </Text>
          <Text
            fontFamily="body"
            fontSize={fontSize}
            color={color}
            marginLeft="4px">
            {`to ${nextTier}`}
          </Text>
        </>
      ) : (
        <Text fontFamily="body" fontSize={fontSize} color={color}>
          {nextTier}
        </Text>
      )}
    </View>
  );
};

export default NextTierCredits;
